import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { encodeParameters } from './encode-parameters';

const NAVIGATION_START_EVENT = 'navigation-start';

interface UpdateUrlParameters {
  anchorLinkId?: string;
  newParameters: Record<string, any>;
  overwrite?: boolean;
  redirectUrl?: string;
  router: AppRouterInstance;
  shouldNavigate?: boolean;
  shouldRefresh?: boolean;
}

/**
 * Updates the URL with new query parameters and optionally navigates to a different page.
 *
 * @param {Object} parameters - The parameters object.
 * @param {boolean} [parameters.overwrite=false] - Optional. If `true`, completely replaces the URL with the new one. If `false`, updates the current URL params with new values keeping old ones as well.
 * @param {AppRouterInstance} parameters.router - The Next.js App Router instance used to navigate or replace the URL.
 * @param {Record<string, any>} parameters.newParameters - An object containing the new parameters to add or merge with the existing URL parameters.
 * @param {string} [parameters.redirectUrl] - Optional. Navigates to the new URL (using `router.push`).
 * @param {boolean} [parameters.shouldNavigate=false] - Optional. If `true`, navigates to the new URL (using `router.push`). If `false`, only updates the current URL without full navigation.
 * @param {boolean} [parameters.shouldRefresh=false] - Optional. If `true`, navigates the URL (using `router.push`) and refreshes the page.
 */

export const updateUrlWithParameters = ({
  anchorLinkId,
  newParameters,
  overwrite = false,
  redirectUrl,
  router,
  shouldNavigate = false,
  shouldRefresh = false,
}: UpdateUrlParameters) => {
  const emitNavigationStart = (targetUrl: string) => {
    const currentUrl = new URL(window.location.href);
    const nextUrl = new URL(targetUrl, currentUrl);

    const currentPathAndSearch = `${currentUrl.pathname}${currentUrl.search}`;
    const nextPathAndSearch = `${nextUrl.pathname}${nextUrl.search}`;

    if (currentPathAndSearch === nextPathAndSearch) {
      return;
    }

    window.dispatchEvent(new CustomEvent(NAVIGATION_START_EVENT));
  };

  const baseUrl = decodeURIComponent(window.location.href)
    .split('?')[0]
    .split('#')[0];
  const currentPath = window.location.pathname;
  const existingQueryString = window.location.search;

  // Extract existing hash from URL if no anchorLinkId is provided
  const existingHash = window.location.hash.replace('#', '');

  const finalAnchor = anchorLinkId || existingHash;

  const existingParameters = new URLSearchParams(existingQueryString);
  const currentParameters: Record<string, string> = {};

  existingParameters.forEach((value, key) => {
    currentParameters[key] = value;
  });

  const mergedParameters = { ...currentParameters, ...newParameters };

  const encodedParameters = encodeParameters(
    overwrite ? newParameters : mergedParameters,
  );

  const redirectedUrl = finalAnchor
    ? `${redirectUrl}?${encodedParameters}#${finalAnchor}`
    : `${redirectUrl}?${encodedParameters}`;

  const refreshedUrl = finalAnchor
    ? `${baseUrl}?${encodedParameters}#${finalAnchor}`
    : `${baseUrl}?${encodedParameters}`;

  const replacedUrl = finalAnchor
    ? `${currentPath}?${encodedParameters}#${finalAnchor}`
    : `${currentPath}?${encodedParameters}`;

  if (shouldNavigate && redirectUrl) {
    emitNavigationStart(redirectedUrl);
    // triggers a real navigation + server render
    router.push(redirectedUrl);
  } else if (shouldRefresh && baseUrl) {
    emitNavigationStart(refreshedUrl);
    // triggers a real navigation + server render
    router.push(refreshedUrl);
  } else {
    emitNavigationStart(replacedUrl);
    // only mutates the browser URL locally, no server redirect is triggered
    window.history.replaceState(null, '', `${replacedUrl}`);
  }
};
